/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { useDebounce } from '../../hooks/use-debounce';
import Spinner from '../../components/Spinner';
import { Link } from 'react-router';
import auth from '../../components/auth';
import ajax from '../../components/ajax';
import helpers from '../../components/helpers';
import { FixedSizeList as List } from 'react-window';
import Box from '@mui/material/Box';
import { PrimaryButton } from '@client/new/components/button';
import { PATHS } from '@client/routes';
import { Select, MenuItem, TextField } from '@mui/material';
import {
  INFLUENCER_BUSINESS_MODEL,
  INFLUENCER_MODES,
  INFLUENCER_TIERS,
  INFLUENCER_VISIBILITY,
  SORT_KEYS,
  useInfluencerListStore,
} from './InfluencerList.store';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import {
  GetInfluencersListQuery,
  useAdminUpdateInfluencerMutation,
  useGetInfluencersListQuery,
} from 'generated/graphql';
import { TitledPage } from '@client/new/components/Misc';
import { CreatorsListStats } from './CreatorsListStats';
import toast from 'react-hot-toast';
import { Tier } from '@client/new/components/Tier/Tier';
import { ArrayItem } from '@client/new/helpers/types';
import _ from 'lodash';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';

const VStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const InfoBox = styled.div`
  margin: 20px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Text = styled.span`
  font-size: ${(props) => props.huge && '1.2rem'};
  font-size: ${(props) => props.big && '1.1rem'};
  font-size: ${(props) => props.small && '0.9rem'};

  font-weight: ${(props) => props.superBold && 700};
  font-weight: ${(props) => props.bold && 500};
`;

const sortInfluencers = (sortKey: string, influencers: Influencers) => {
  switch (sortKey) {
    case SORT_KEYS.PUBLISHED_AT.value:
      return influencers.sort(
        (inf1, inf2) =>
          new Date(inf2.publishedAt).getTime() -
          new Date(inf1.publishedAt).getTime(),
      );
    case SORT_KEYS.CREATED_AT.value:
      return influencers.sort(
        (inf1, inf2) =>
          new Date(inf2.createdAt).getTime() -
          new Date(inf1.createdAt).getTime(),
      );
    case SORT_KEYS.NAME.value:
      return _.sortBy(influencers, ['firstName', 'lastName']);
    default:
      return influencers;
  }
};

const filterByTier = (tierKey: number, influencers: Influencers) => {
  if (tierKey === 999) {
    // TODO - Figure out better way to handle
    return influencers;
  }
  if (Number.isNaN(tierKey)) {
    return influencers.filter((influencer) => !influencer.tier);
  }
  const filteredInfluencers = influencers.filter(
    (influencer) => influencer.tier === tierKey,
  );
  return filteredInfluencers;
};

const filterByVisibility = (
  visibility: ArrayItem<Influencers>['visibility'],
  influencers: Influencers,
) => {
  if (!visibility) {
    return influencers;
  }
  const filteredInfluencers = influencers.filter(
    (influencer) => influencer.visibility === visibility,
  );
  return filteredInfluencers;
};

const filterByBusinessModel = (
  businessModel: ArrayItem<Influencers>['businessModel'],
  influencers: Influencers,
) =>
  !businessModel
    ? influencers
    : influencers.filter(
        (influencer) => influencer.businessModel === businessModel,
      );

const CreatorRowColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`;

const visibilityLabel = (visibility: ArrayItem<Influencers>['visibility']) => {
  if (visibility === 'exclusive') return 'PPC';
  return visibility;
};

const MAX_LENGTH = 23;
type InfluencerListItemProps = {
  onChange: () => void;
  influencer: ArrayItem<Influencers>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: React.MouseEventHandler<any>;
  style: React.CSSProperties;
};
const InfluencerListItem = ({
  onChange,
  influencer,
  style,
  ...p
}: InfluencerListItemProps) => {
  const [submitting, setSubmitting] = useState(false);
  const isSortByCreatedAt = useInfluencerListStore(
    (state) => state.filters.sortBy == SORT_KEYS.CREATED_AT.value,
  );

  const deleteInfluencer = (id: number) => {
    const answer = confirm('Are you sure you want to *delete* this Partner?');
    if (answer) {
      ajax
        .deleteInfluencer(id)
        .then(() => {
          onChange();
        })
        .catch((e) => {
          console.log('[InfluencerListItem] Error deleting Partner.', e);
          if (e === 'auth') {
            auth.logout();
          }
        });
    }
  };
  const [adminUpdateInfluencerMutation] = useAdminUpdateInfluencerMutation();

  const onTierChange = async (tier: number) => {
    setSubmitting(true);
    try {
      adminUpdateInfluencerMutation({
        variables: {
          data: {
            id: influencer.id,
            tier,
          },
        },
      });
      toast.success('Tier updated successfully!');
    } catch (e) {
      toast.error('Something went wrong!');
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const influencerFullName = `${influencer.firstName} ${influencer.lastName}`;
  const influencerFullNameFormatted =
    influencerFullName.length > MAX_LENGTH + 3
      ? influencerFullName.substring(0, MAX_LENGTH) + '...'
      : influencerFullName;

  function resizeS3Image(path: string, width: number, height = 0) {
    return `${
      import.meta.env.VITE_IMAGE_HANDLER_URL
    }/${width}x${height}/${path}`;
  }

  const influencerAvatar = influencer.mediaCover
    ? resizeS3Image(influencer.mediaCover.image, 40, 40)
    : import.meta.env.BASE_URL + 'static/images/user-icon@2x.png';

  return (
    <div
      id="influencer-list-item"
      style={style}
      className="group flex flex-row lg:flex-col justify-between items-center gap-5 lg:gap-0 lg:grid lg:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] lg:content-center bg-white  first-of-type:rounded-t-[10px] last-of-type:rounded-b-[10px] shadow-sm p-4 hover:bg-slate-200 hover:cursor-pointer transition-colors duration-150"
    >
      <Link
        to={PATHS.HOME_CREATOR(influencer.id)}
        className="text-black"
        onClick={p.onClick}
      >
        <CreatorRowColumn>
          <Avatar src={influencerAvatar} loading="lazy" />
          <div className="flex flex-col">
            <Text superBold huge>
              {influencerFullNameFormatted} {`(${influencer.id})`}
            </Text>
            <Text className="text-gray-4 text-sm">
              {isSortByCreatedAt
                ? influencer?.createdAt
                  ? `Created ${helpers.dateToStringSimple(
                      influencer.createdAt,
                    )}`
                  : null
                : influencer.publishedAt
                ? `Published ${helpers.dateToStringSimple(
                    influencer.publishedAt,
                  )}`
                : null}
            </Text>
          </div>
        </CreatorRowColumn>
      </Link>
      <CreatorRowColumn className="hidden lg:inline">
        {influencer.publishedAt && (
          <Text superBold className="capitalize">
            {visibilityLabel(influencer.visibility)}
          </Text>
        )}
      </CreatorRowColumn>
      <CreatorRowColumn className="hidden lg:inline">
        <Text superBold className="capitalize">
          {influencer.businessModel}
        </Text>
      </CreatorRowColumn>
      <CreatorRowColumn className="hidden lg:inline">
        <CreatorsListStats
          profileInstagram={influencer.profileInstagram}
          instagramFollowersCount={influencer.instagramFollowersCount}
          profileTiktok={influencer.profileTiktok}
          profileYoutubeChannel={influencer.profileYoutubeChannel}
        />
      </CreatorRowColumn>
      <CreatorRowColumn className="hidden lg:inline">
        <Tier
          value={influencer.tier}
          onChange={onTierChange}
          loading={submitting}
        />
      </CreatorRowColumn>
      <CreatorRowColumn className="lg:justify-end">
        <ul className="flex flex-row gap-3">
          {auth.isAdministrator() && (
            <div className="ml-2 lg:ml-5 pl-4 lg:pl-5">
              <li>
                <a
                  className="actionLink text-gray-400 text-xl leading-normal"
                  onClick={() => {
                    deleteInfluencer(influencer.id);
                  }}
                  id={`del-${influencer.firstName}-${influencer.lastName}`}
                >
                  <div className="flex pr-1 text-[#9E9E9E] text-[19px] justify-center items-center gap-2">
                    <FontAwesomeIcon icon={faTrash} />
                  </div>
                </a>
              </li>
            </div>
          )}
        </ul>
      </CreatorRowColumn>
    </div>
  );
};

const SearchBar = ({
  activeMode,
  searchKey,
  tierKey,
  sortKey,
  visibilityKey,
  businessModelKey,
  onSearchChange,
  onModeChange,
  onSortChange,
  onTierChange,
  onVisibilityChange,
  onBusinessModelChange,
}: {
  activeMode: number;
  searchKey: string;
  tierKey: number;
  sortKey: string;
  visibilityKey: string;
  businessModelKey: string;
  onSearchChange: (searchKey: string) => void;
  onModeChange: (mode: number | null) => void;
  onSortChange: (sortKey: string) => void;
  onTierChange: (tierKey: number) => void;
  onVisibilityChange: (visibilityKey: string) => void;
  onBusinessModelChange: (businessModelKey: string) => void;
}) => {
  return (
    <div className="grid grid-cols-2 grid-rows-2 lg:w-auto lg:flex gap-3 lg:flex-row lg:items-center lg:content-start">
      <VStack>
        <label>Search by name</label>
        <form className="relative">
          <TextField
            className="w-full"
            value={searchKey}
            onChange={(e) => onSearchChange(e.target.value)}
            id="search-input"
          />
        </form>
      </VStack>
      <VStack>
        <label>Sort by</label>
        <Select value={sortKey} onChange={(e) => onSortChange(e.target.value)}>
          {Object.values(SORT_KEYS).map((sortKey, index) => {
            return (
              <MenuItem key={index} value={sortKey.value}>
                {sortKey.name}
              </MenuItem>
            );
          })}
        </Select>
      </VStack>
      <VStack>
        <label>Tier</label>
        <Select
          value={tierKey}
          onChange={(e) => onTierChange(e.target.value as number)}
        >
          {INFLUENCER_TIERS.map((tier, index) => {
            return (
              <MenuItem key={index} value={tier.value}>
                {tier.name}
              </MenuItem>
            );
          })}
        </Select>
      </VStack>
      <VStack>
        <label>Subscription type</label>
        <Select
          value={visibilityKey}
          onChange={(e) => onVisibilityChange(e.target.value)}
          displayEmpty={true}
        >
          {INFLUENCER_VISIBILITY.map((item, index) => {
            return (
              <MenuItem key={index} value={item.value}>
                {item.name}
              </MenuItem>
            );
          })}
        </Select>
      </VStack>
      <VStack>
        <label>Stage</label>
        <Select
          value={activeMode}
          onChange={(e) => onModeChange(e.target.value as number)}
          displayEmpty={true}
        >
          {INFLUENCER_MODES.map((mode, index) => {
            return (
              <MenuItem key={index} value={mode.value}>
                {mode.name}
              </MenuItem>
            );
          })}
        </Select>
      </VStack>
      <VStack>
        <label>Business model</label>
        <Select
          value={businessModelKey}
          onChange={(e) => onBusinessModelChange(e.target.value)}
          displayEmpty={true}
        >
          {INFLUENCER_BUSINESS_MODEL.map((item, index) => {
            return (
              <MenuItem key={index} value={item.value}>
                {item.name}
              </MenuItem>
            );
          })}
        </Select>
      </VStack>
    </div>
  );
};

const storageSearchKey = 'dashboardSearchKey';

type Influencers = GetInfluencersListQuery['creators'];
const InfluencerListWithHooks = () => {
  const [searchKey, setSearchKey] = useState('');
  const searchKeyDebounced = useDebounce(searchKey, 200);
  const store = useInfluencerListStore();

  const [sortedInfluencers, setSortedInfluencers] = useState([]);

  const query = useGetInfluencersListQuery({
    variables: { mode: store.filters.mode },
    fetchPolicy: 'cache-and-network',
  });
  const { loading: isQueryLoading, error, data, refetch } = query;

  useEffect(() => {
    const savedSearchKey = sessionStorage.getItem(storageSearchKey);
    if (savedSearchKey) {
      setSearchKey(savedSearchKey);
      filterLists(savedSearchKey);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(storageSearchKey, searchKey);
  }, [searchKey]);

  useEffect(() => {
    filterLists(searchKeyDebounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.creators, store.filters, searchKeyDebounced]);

  const filterLists = (searchKeyParam: string) => {
    const influencers: Influencers = data ? [...data.creators] : [];

    const search = searchKeyParam.toLocaleLowerCase();
    const filteredInfluencers = [
      (influencers) =>
        filterByBusinessModel(store.filters.businessModel, influencers),
      (influencers) =>
        filterByVisibility(store.filters.visibility, influencers),
      (influencers) => filterByTier(store.filters.tier, influencers),
      (influencers) =>
        searchKeyParam.length
          ? influencers.filter((inf: ArrayItem<Influencers>) =>
              `${inf.firstName} ${inf.lastName} ${inf.customURL} ${inf.id}`
                .toLowerCase()
                .includes(search),
            )
          : influencers,
    ].reduce((acc, next) => next(acc), influencers);

    const sorted = sortInfluencers(store.filters.sortBy, filteredInfluencers);

    setSortedInfluencers(sorted);
  };

  const actionsPanel = (
    <div className="box">
      <div className="button-box">
        <Link to={PATHS.ADMIN_NEW_CREATOR}>
          <PrimaryButton>Add New Creator</PrimaryButton>
        </Link>
      </div>
    </div>
  );

  return (
    <TitledPage title="Creators" rightContent={actionsPanel}>
      <main id="main">
        <div>
          <SearchBar
            activeMode={store.filters.mode}
            sortKey={store.filters.sortBy}
            searchKey={searchKey}
            tierKey={store.filters.tier}
            visibilityKey={store.filters.visibility}
            businessModelKey={store.filters.businessModel}
            onModeChange={(mode) => store.setFilter('mode', mode)}
            onSearchChange={setSearchKey}
            onTierChange={(tier) => store.setFilter('tier', tier)}
            onSortChange={(sortKey) => store.setFilter('sortBy', sortKey)}
            onVisibilityChange={(visibility) =>
              store.setFilter('visibility', visibility)
            }
            onBusinessModelChange={(businessModel) =>
              store.setFilter('businessModel', businessModel)
            }
          />
          {error && <p>Could not fetch creators: {error.message}</p>}
          {isQueryLoading && (
            <InfoBox>
              <Spinner color="#6367EF" />
            </InfoBox>
          )}
          {!isQueryLoading && !error && (
            <InfluencersVirtualList
              refetch={refetch}
              sortedInfluencers={sortedInfluencers}
            />
          )}
        </div>
      </main>
    </TitledPage>
  );
};

export default InfluencerListWithHooks;

function InfluencersVirtualList({
  sortedInfluencers,
  refetch,
}: {
  sortedInfluencers: Influencers;
  refetch: () => void;
}) {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const InfluencerList = ({ influencers }: { influencers: Influencers }) => (
    <>
      <List
        height={windowHeight - 300}
        itemCount={influencers.length}
        itemSize={83.98}
        width={'100%'}
        className="mt-2.5"
      >
        {(props: any) => {
          const influencer = influencers[props.index];
          return (
            <InfluencerListItem
              key={props.index}
              influencer={influencer}
              onChange={refetch}
              style={props.style}
              onClick={() =>
                trackSessionEvent('ImpersonateCreator', {
                  influencerId: influencer.id,
                  name: `${influencer.firstName} ${influencer.lastName} /${influencer.customURL}`,
                })
              }
            />
          );
        }}
      </List>
    </>
  );

  return (
    <Box sx={{ width: '100%' }} id="influencers-list">
      {sortInfluencers.length > 0 ? (
        <InfluencerList influencers={sortedInfluencers} />
      ) : (
        <h2>No results</h2>
      )}
    </Box>
  );
}
